import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
	graphql,
	usePaginationFragment,
	usePreloadedQuery,
	type EntryPointProps,
} from 'react-relay';
import Spinner from '@atlaskit/spinner';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { IssueTypeEditViewWithIssueTypeConnection } from '@atlassian/jira-issue-field-issue-type-editview-full/src/ui/issue-type/index.tsx';
import type { Option } from '@atlassian/jira-issue-field-issue-type-editview-full/src/ui/issue-type/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { IssueCreateIssueType, IssueCreateProject } from '../../../common/types.tsx';
import { useDefaultIssueType } from '../../../controllers/config/selectors.tsx';
import type {
	IssueTypeDropdownQuery,
	JiraIssueTypeFilterInput,
} from './__generated__/IssueTypeDropdownQuery.graphql';
import type { IssueTypeDropdown$key } from './__generated__/IssueTypeDropdown.graphql';
import issueTypeDropdownInlineIssueCreateRefetchPaginatedQuery, {
	type issueTypeDropdown_InlineIssueCreate_RefetchPaginated_Query,
} from './__generated__/issueTypeDropdown_InlineIssueCreate_RefetchPaginated_Query.graphql';
import messages from './messages.tsx';

const createIssueTypeOption = (
	id: string,
	name: string,
	avatar: string | null | undefined,
	issueTypeId: string | null | undefined,
): Option => ({
	label: name,
	value: id,
	avatar: {
		small: avatar,
	},
	issueTypeId: fg('work_type_inline_config_nin_icc_fg') ? issueTypeId || undefined : undefined,
});

const createIssueType = (
	id: string,
	issueTypeId: string,
	name: string,
	avatar: string | null | undefined,
	hierarchyLevel: number | null | undefined,
): IssueCreateIssueType => ({
	id,
	issueTypeId,
	name,
	avatar: {
		small: avatar,
	},
	...(typeof hierarchyLevel === 'number' ? { hierarchy: { level: hierarchyLevel } } : {}),
});

export type Props = {
	onChange: (selectedType: IssueCreateIssueType | null, isSelected?: boolean) => void;
	onProjectChange: (project: IssueCreateProject | null) => void;
	setIsEditing?: (value: boolean) => void;
	setIsManageIssueTypesModalOpen?: (value: boolean) => void;
	isManageIssueTypesModalOpen?: boolean;
	selectedType: IssueCreateIssueType | null;
	project: IssueTypeDropdown$key | null;
	isSearchable?: boolean;
	filter?: JiraIssueTypeFilterInput | null | undefined;
};

// eslint-disable-next-line jira/export/restrict-types-re-export
export type IssueTypeOption = Option;

const placeholder = <Spinner size="medium" />;

// TODO: Create a base component and extend the base to have different UI components -- One with only icons
// for select value and one with both icons and labels for the select value.
export const IssueTypeDropdown = ({
	onChange,
	onProjectChange,
	setIsEditing,
	setIsManageIssueTypesModalOpen,
	isManageIssueTypesModalOpen,
	project,
	selectedType,
	isSearchable,
	filter,
}: Props) => {
	const { formatMessage } = useIntl();
	const isPristine = useRef(true);

	// TODO; JSC-36  We need to integrate hierarchy level filter once JSC-59 is implemented
	const { data, refetch } = usePaginationFragment<
		issueTypeDropdown_InlineIssueCreate_RefetchPaginated_Query,
		IssueTypeDropdown$key
	>(
		graphql`
			fragment IssueTypeDropdown on JiraProject
			@argumentDefinitions(
				cursor: { type: "String" }
				first: { type: "Int", defaultValue: 10 }
				filter: { type: "JiraIssueTypeFilterInput" }
				isInlineManageWorkTypesConfigNinIccEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-nin-inline-manage-work-type-config.relayprovider"
				}
			)
			@refetchable(queryName: "issueTypeDropdown_InlineIssueCreate_RefetchPaginated_Query") {
				id
				projectId
				key
				name
				avatar {
					large
				}
				issueTypes(first: $first, after: $cursor, filter: $filter)
					@connection(key: "inlineIssueCreate_IssueTable_issueTypes") {
					edges {
						node {
							id
							issueTypeId
							name
							avatar {
								small
							}
							hierarchy {
								level
							}
						}
					}
					...issueType_issueFieldIssueTypeEditviewFull_IssueTypeEditViewWithIssueTypeConnection_issueTypesFragmentRef
				}
				...issueType_issueFieldIssueTypeEditviewFull_IssueTypeEditViewWithIssueTypeConnection_permissionsFragmentRef
					@include(if: $isInlineManageWorkTypesConfigNinIccEnabled)
			}
		`,
		project,
	);

	const [defaultIssueTypeId, { setDefaultIssueType }] = useDefaultIssueType(data?.key ?? null);

	const selectedTypeOption = useMemo<IssueTypeOption | null>(() => {
		if (selectedType == null) {
			return null;
		}
		return createIssueTypeOption(
			selectedType.id,
			selectedType.name,
			selectedType.avatar?.small,
			selectedType.issueTypeId,
		);
	}, [selectedType]);

	const handleGetRefetchVariables = useCallback(
		// @ts-expect-error - isInlineManageWorkTypesConfigNinIccEnabled is automatically injected at runtime by the Relay provider
		(): issueTypeDropdown_InlineIssueCreate_RefetchPaginated_Query['variables'] => ({
			id: data?.id || '',
		}),
		[data?.id],
	);

	const handleIssueTypeChange = useCallback(
		(typeOption: IssueTypeOption | null) => {
			let newIssueType: IssueCreateIssueType | null = null;
			if (typeOption != null) {
				const issueType = (data?.issueTypes?.edges || []).find(
					(edge) => edge?.node?.id === typeOption.value,
				);
				if (issueType?.node != null && issueType.node.issueTypeId != null) {
					newIssueType = createIssueType(
						issueType.node.id,
						issueType.node.issueTypeId,
						issueType.node.name,
						issueType.node.avatar?.small,
						issueType.node.hierarchy?.level,
					);
				}
			}
			isPristine.current = false;
			onChange(newIssueType, fg('yurree_jjj2_timeline') ? true : undefined);

			if (newIssueType?.issueTypeId) {
				setDefaultIssueType(data?.key ?? null, newIssueType?.issueTypeId);
			}
		},
		[data?.issueTypes?.edges, data?.key, onChange, setDefaultIssueType],
	);

	// Initialize with the first default issue
	useEffect(() => {
		if (isPristine.current) {
			const defaultIssueTypeEdge = (data?.issueTypes?.edges || []).find(
				(edge) => edge?.node?.issueTypeId === defaultIssueTypeId,
			);

			if (defaultIssueTypeEdge?.node != null && defaultIssueTypeEdge.node.issueTypeId != null) {
				// Return default issue type if exists
				// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
				return onChange(
					createIssueType(
						defaultIssueTypeEdge.node.id,
						defaultIssueTypeEdge.node.issueTypeId,
						defaultIssueTypeEdge.node.name,
						defaultIssueTypeEdge.node.avatar?.small,
						defaultIssueTypeEdge.node.hierarchy?.level,
					),
				);
			}
			const firstIssue = data?.issueTypes?.edges?.[0];
			if (firstIssue?.node != null && firstIssue.node.issueTypeId != null) {
				// Return first issue type in the connection
				// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
				return onChange(
					createIssueType(
						firstIssue.node.id,
						firstIssue.node.issueTypeId,
						firstIssue.node.name,
						firstIssue.node.avatar?.small,
						firstIssue.node.hierarchy?.level,
					),
				);
			}
		}
	}, [data?.issueTypes?.edges, defaultIssueTypeId, onChange]);

	const prevProjectKey = useRef<string | null>(null);

	useEffect(() => {
		if (data?.id != null && prevProjectKey.current !== data?.id && data.projectId != null) {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
			onProjectChange({
				id: data.id,
				projectId: data.projectId,
				key: data.key,
				name: data.name,
				avatar: data.avatar
					? {
							large: data.avatar.large,
						}
					: null,
			});
			prevProjectKey.current = data?.id;
		}
	}, [data?.avatar, data?.id, data?.key, data?.name, data?.projectId, onProjectChange]);

	const manageIssueTypeProps = useMemo(() => {
		if (!data?.key || !data?.projectId || !fg('work_type_inline_config_nin_icc_fg')) {
			return undefined;
		}

		const { minHierarchyLevel, maxHierarchyLevel } = filter || {};

		return {
			projectKey: data.key,
			projectId: data.projectId,
			// We don't allow issue type creation if level 0 issues are excluded from the dropdown
			allowIssueTypeCreation:
				(!minHierarchyLevel || minHierarchyLevel <= 0) &&
				(!maxHierarchyLevel || maxHierarchyLevel >= 0),
			setIsManageIssueTypesModalOpen,
		};
	}, [data?.key, data?.projectId, filter, setIsManageIssueTypesModalOpen]);

	// TODO: JSC-71 We need to updated below component styles to adapt more to JWM current design
	return (
		<IssueTypeEditViewWithIssueTypeConnection
			issueTypesFragmentRef={data?.issueTypes ?? null}
			projectFragmentRef={
				manageIssueTypeProps && fg('work_type_inline_config_nin_icc_fg') ? data : undefined
			}
			menuPosition="fixed"
			menuPlacement="top"
			onChange={handleIssueTypeChange}
			onRefetch={refetch}
			onGetRefetchVariables={handleGetRefetchVariables}
			setIsEditing={setIsEditing}
			refetchQuery={issueTypeDropdownInlineIssueCreateRefetchPaginatedQuery}
			value={selectedTypeOption}
			spacing="compact"
			stopPropagationOfMenuEscape
			label={formatMessage(
				fg('jira-issue-terminology-update-m4')
					? messages.labelIssueTerminologyRefreshM4
					: messages.label,
			)}
			iconsOnly
			appearance="subtle"
			isSearchable={isSearchable}
			placeholder={fg('jifjam_deliveroo_icc_changes') ? placeholder : undefined}
			manageIssueTypeProps={
				// eslint-disable-next-line jira/ff/no-preconditioning
				fg('work_type_inline_config_nin_icc_fg') &&
				expVal('work_type_inline_config_nin_icc', 'isEnabled', false)
					? manageIssueTypeProps
					: undefined
			}
			isManageIssueTypesModalOpen={
				fg('work_type_inline_config_nin_icc_fg') ? isManageIssueTypesModalOpen : undefined
			}
			renderAsPopupSelect={fg('jifjam2_icc_fix')}
		/>
	);
};

type Queries = {
	projectData: IssueTypeDropdownQuery;
};

export const IssueTypeDropdownEntryPoint = ({
	queries: { projectData },
	props,
}: EntryPointProps<Queries, {}, Omit<Props, 'project'>, {}>) => {
	const data = usePreloadedQuery<IssueTypeDropdownQuery>(
		graphql`
			query IssueTypeDropdownQuery(
				$cloudId: ID!
				$projectKey: String!
				$hasProjectKey: Boolean!
				$filter: JiraIssueTypeFilterInput
			) {
				jira @required(action: THROW) {
					jiraProjectByKey(cloudId: $cloudId, key: $projectKey)
						@required(action: THROW)
						@include(if: $hasProjectKey) {
						...IssueTypeDropdown @arguments(filter: $filter)
					}
				}
			}
		`,
		projectData,
	);

	return (
		<IssueTypeDropdown
			{...props}
			project={data.jira.jiraProjectByKey ?? null}
			filter={fg('work_type_inline_config_nin_icc_fg') ? projectData.variables.filter : undefined}
		/>
	);
};
export default IssueTypeDropdown;
